import { useState, useEffect } from 'react'

import Plot from 'react-plotly.js';

import gSession from '../script/session';
import gUtilities from '../script/utilities';
import gDataStore from "../script/data_store.js";
import TableWidget from './TableWidget';

//
// DVH and histogram plot. Uses Plotly.
// npm install react-plotly.js plotly.js
// https://plotly.com/javascript/react/
//
const DvhPage = () => {
    const [job, setJob] = useState(null);
    const [plot, setPlot] = useState(null);
    const [table, setTable] = useState(null);
    const [dicom, setDicom] = useState(null);
    const [banner, setBanner] = useState([{
        fingerprint: "No job",
        plan_name: "No job",
        state: "No job",
        machine_name: "No job",
        simulator_name: "No job"
    }]);
    const [prescription, setPrescription] = useState(null);

    let current_job = gDataStore.retrieve("CurrentJob", null);
    if (!job && current_job) {
        setJob(current_job);
        updateBanner(current_job);
    } else if (current_job && current_job.job_id !== job.job_id) {
        setJob(current_job);
        updateBanner(current_job);
    }

    function onCurrentJob(job) {
        setJob(job);
        updateBanner(job);
    }
    gDataStore.onStore("CurrentJob", onCurrentJob);

    const banner_columns = ["fingerprint", "plan_name", "state", "machine_name", "simulator_name"];
    const banner_titles = ["Fingerprint", "Plan Name", "State", "Machine", "Simulator"];

    function updateBanner(job) {
        setBanner([{
            fingerprint: gUtilities.fingerprint(job.job_id),
            plan_name: job.plan.plan_name,
            state: job.state,
            machine_name: job.machine_name,
            simulator_name: job.simulator_name
        }]);
    }

    function plotDvh(roi_name, dvh) {
        let curves = [
            {
                x: dvh.dose,
                y: dvh.dvh,
                mode: "lines",
                type: "scatter",
                name: `${roi_name} DVH`,
                line: {
                    color: "red"
                },
                yaxis: 'y'
            },
            {
                x: dvh.dose,
                y: dvh.histogram,
                mode: "lines",
                type: "scatter",
                name: `${roi_name} Histogram`,
                line: {
                    color: "black"
                },
                yaxis: "y2"
            }
        ];

        let layout = {
            xaxis: { title: "Dose Gy" },
            yaxis: { title: "ROI Volume %" },
            yaxis2: { title: "Histogram", color: "black", overlaying: 'y', side: "right" },
            title: "DVH"
        };

        let data = {
            curves: curves,
            layout: layout
        };

        setPlot(data);
    }

    function buildTable(data) {
        let tb = {
            columns: ["bin", "dose", "dvh", "percent", "histogram", "total"],
            titles: ["Bin", "Dose", "Dvh", "Percent", "Histogram", "Total"],
            data: []
        };

        let row;
        for (let i = 0; i < data.bin.length; ++i) {
            row = {
                "bin": data.bin[i],
                "dose": data.dose[i],
                "dvh": data.dvh[i],
                "percent": data.percent[i],
                "histogram": data.histogram[i],
                "total": data.total[i]
            };

            tb.data.push(row);
        }

        setTable(tb);
    }

    function addCsvRow(csv, row) {
        return csv + row.join(',') + '\n';
    }

    function onCSV() {
        let csv = addCsvRow("", table.titles);

        for (let row of table.data) {
            row = [
                row.bin,
                row.dose,
                row.dvh,
                row.percent,
                row.histogram,
                row.total
            ];

            csv = addCsvRow(csv, row);
        }

        let url = URL.createObjectURL(
            new Blob([csv], { type: "text/plaintext" })
        );

        window.open(url, "_blank", "noopener noreferrer");
    }

    async function getDicom() {
        let request = {
            service: "db.query",
            collection: "dcs_job_rsdicom",
            many: false,
            query: {
                "request.job_id": job.job_id
            }
        };

        let response = await gSession.exchange(request);

        if (response.isError) {
            gUtilities.showError("DVH Plot", response.message);
        } else {
            setDicom(response);
        }
    }

    async function getPrescription() {
        let request = {
            service: "db.query",
            collection: "dcs_prescription",
            many: false,
            query: {
                job_id: job.job_id
            }
        };

        let response = await gSession.exchange(request);

        if (response.isError) {
            gUtilities.showError("Prescription", response.message);
        } else {
            setPrescription(response);
        }
    }

    async function getJsonFromBucket(key) {
        let request = {
            command: "agent.file-contents",
            bucket: job.files.bucket,
            key: key
        };

        let response = await gSession.exchange(request);

        if (response.isError) {
            gUtilities.showError("Load ROI Data", response.message);
            return;
        }

        let buffer = atob(response.contents);
        let text = buffer.toString("utf-8");

        let data = JSON.parse(text);

        return data;
    }

    async function onSelectRoi(name) {
    }

    function getSelectedRoiName() {
        let widget = document.querySelector("#w_RoiSelect");

        return widget.value;
    }

    async function onPlot() {
        let name = getSelectedRoiName();

        let roi_key = null;
        for (let key of prescription.dvh_files.keys)
            if (key.tag === name) {
                roi_key = key;
                break;
            }

        if (!roi_key) {
            gUtilities.showError("Lookup ROI", "Failed to find ROI");
            return;
        }

        let data = await getJsonFromBucket(roi_key.value);

        plotDvh(name, data);
        buildTable(data);
    }

    useEffect(() => {
        getDicom();
        getPrescription();
    }, [job]);

    return (
        <div>
            <div className="alert alert-dismissible alert-warning">
                <h4 className="alert-heading">Prescription</h4>
                <TableWidget name="Banner" data={banner} columns={banner_columns} titles={banner_titles} />
            </div>
            <div className="container">
                <div className="row">
                    {false &&
                        <fieldset className="form-group col-sm">
                            <legend className="mt-4">Data Source</legend>
                            <div className="container">
                                <div className="row">
                                    <div className="form-check col-sm">
                                        <label className="form-check-label">
                                            <input type="radio" className="form-check-input" name="DvhSourceRadios" id="w_DvhMctalRadio" value="dvh_source_mctal"></input>
                                            Tally
                                        </label>
                                    </div>
                                    <div className="form-check col-sm">
                                        <label className="form-check-label">
                                            <input type="radio" className="form-check-input" name="DvhSourceRadios" id="w_DvhDicomRadio" value="dvh_source_dicom"></input>
                                            Dose
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    }
                    {false &&
                        <fieldset className="form-group col-sm">
                            <legend className="mt-4">Beam Number</legend>
                            <div className="container">
                                <div className="row">
                                    <div className="form-check col-sm">
                                        <label className="form-check-label">
                                            <input type="radio" className="form-check-input" name="DvhBeamRadios" id="w_DvhBeam1Radio" value="dvh_beam_1" ></input>
                                            Beam 1
                                        </label>
                                    </div>
                                    <div className="form-check col-sm">
                                        <label className="form-check-label">
                                            <input type="radio" className="form-check-input" name="DvhBeamRadios" id="w_DvhBeam2Radio" value="dvh_beam_2"></input>
                                            Beam 2
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    }
                    {dicom &&
                        <div className="col-sm">
                            <label htmlFor="w_RoiSelect" className="form-label mt-4">ROI</label>
                            <div className="input-group mb-3">
                                <select className="form-select" onChange={onSelectRoi} id="w_RoiSelect" aria-describedby="w_PlotButton">
                                    {
                                        dicom.rois.map((roi) => (
                                            roi.valid &&
                                            <option>{roi.name}</option>
                                        ))
                                    }
                                </select>
                                <button type="button" className="btn btn-primary" id="w_PlotButton" onClick={onPlot}>Plot</button>
                            </div>
                        </div>
                    }
                </div>
            </div>
            {plot &&
                <>
                    <div>
                        <Plot data={plot.curves} layout={plot.layout} />
                    </div>
                    <br></br>
                    <div>
                        {table &&
                            <>
                                <h2>DVH and Histogram Table <button type="button" class="btn btn-link" onClick={onCSV}>CSV</button></h2>
                                <TableWidget name="DVHTable" data={table.data} columns={table.columns} titles={table.titles} />
                            </>
                        }
                    </div>
                </>
            }
        </div>
    );
}

export default DvhPage;